import "./AvatarModal.scss";
import { useState, useEffect } from "react";
import useAuthStore from "../../../../store/authStore";
import md5 from "md5";
import { useTranslation, Trans } from "react-i18next";

function AvatarModal({ onClose }) {
  const { t } = useTranslation();
  const { user, token, updateUser } = useAuthStore();
  // Check if user currently has useGravatar enabled
  const [useGravatar, setUseGravatar] = useState(
    user?.profile?.useGravatar || false
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [gravatarUrl, setGravatarUrl] = useState("");

  // Generate Gravatar URL from email
  useEffect(() => {
    if (user?.email) {
      const emailHash = md5(user.email.toLowerCase().trim());
      const url = `https://www.gravatar.com/avatar/${emailHash}?s=200&d=mp`;
      setGravatarUrl(url);
    }
  }, [user?.email]);

  const handleToggle = () => {
    setUseGravatar(!useGravatar);
  };

  const handleSave = async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/auth/user/${user.id || user._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            firstName: user.firstName,
            lastName: user.lastName,
            profile: {
              ...user.profile,
              useGravatar: useGravatar,
            },
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update avatar");
      }

      const updatedUser = await response.json();

      // Update the user in the store
      updateUser(updatedUser);

      // Close modal
      if (onClose) {
        onClose();
      }
    } catch (err) {
      console.error("Error updating avatar:", err);
      setError("avatar.updateFailed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <div className="avatar-modal">
      <div className="avatar-modal-header">
        <h3>{t("avatar.title")}</h3>
        <button className="close-button" onClick={handleCancel}>
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>

      <div className="avatar-modal-content">
        <div className="avatar-info">
          <p>
            <Trans
              i18nKey="avatar.intro"
              values={{ email: user?.email }}
              components={{
                link: (
                  <a
                    href="https://gravatar.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
                strong: <strong />,
              }}
            />
          </p>
          <ol>
            <li>
              <Trans
                i18nKey="avatar.step1"
                components={{
                  link: (
                    <a
                      href="https://gravatar.com"
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ),
                }}
              />
            </li>
            <li>{t("avatar.step2")}</li>
            <li>{t("avatar.step3")}</li>
          </ol>
        </div>

        <div className="avatar-preview">
          {useGravatar ? (
            <img
              src={gravatarUrl}
              alt={t("avatar.previewAlt")}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            <div
              className="initials-preview"
              style={{
                backgroundColor: user?.profile?.color || "#2196f3",
              }}
            >
              {user?.firstName?.charAt(0).toUpperCase()}
              {user?.lastName?.charAt(0).toUpperCase()}
            </div>
          )}
        </div>

        <div className="toggle-container">
          <div className="toggle-label">
            <span>{t("avatar.useGravatar")}</span>
            <p className="toggle-help">
              {useGravatar
                ? t("avatar.usingGravatar")
                : t("avatar.usingInitials")}
            </p>
          </div>
          <button
            className={`toggle-button ${useGravatar ? "active" : ""}`}
            onClick={handleToggle}
            disabled={isLoading}
          >
            <div className="toggle-slider"></div>
          </button>
        </div>

        {error && <div className="error-message">{t(error)}</div>}
      </div>

      <div className="avatar-modal-actions">
        <button
          className="cancel-button"
          onClick={handleCancel}
          disabled={isLoading}
        >
          {t("common.cancel")}
        </button>
        <button
          className="update-button"
          onClick={handleSave}
          disabled={isLoading}
        >
          {isLoading ? t("common.saving") : t("common.save")}
        </button>
      </div>
    </div>
  );
}

export default AvatarModal;
